import { siteConfig, socialConfig } from "@acme/config";
import { toPlainText } from "@acme/mdx/lint";
import {
  type CapabilityId,
  getCapabilities,
} from "~/lib/constants/capabilities";
import type { FaqItem } from "~/lib/constants/faq";
import { getProcessPhases } from "~/lib/constants/process";
import { getServicePages } from "~/lib/constants/service-pages";
import type { ServicePage } from "~/lib/constants/service-pages/types";
import * as m from "~/paraglide/messages.js";
import { baseLocale, getLocale, locales } from "~/paraglide/runtime.js";
import { getBaseUrl } from "./utils";

function getInLanguage(locale: string = getLocale()): string {
  return locale === "lv" ? "lv-LV" : "en-US";
}

/**
 * Absolute URL for a page-level schema node. `path` is always de-localized
 * (`/about`, `/blog/<slug>`); the prefix comes from `locale` alone.
 *
 * Page nodes have to agree with the `<link rel="canonical">` beside them. They
 * did not: `/lv/about` declared a canonical of `…/lv/about` while its
 * `AboutPage` node claimed `…/about`, on every localized page.
 *
 * `locale` defaults to the request locale, which is right for static pages.
 * Content routes must pass the locale they *resolved* instead, because the two
 * differ exactly when a translation is missing: `/lv/blog/<slug>` for an
 * English-only article serves the English text and canonicalises to the
 * English URL, so the schema has to say English too.
 *
 * Site-wide entity ids (organization, person, website) deliberately do not use
 * this: there is one of each per site, not one per locale.
 */
function localizedSchemaUrl(
  path: string,
  locale: string = getLocale()
): string {
  const baseUrl = getBaseUrl();
  return locale === baseLocale
    ? `${baseUrl}${path}`
    : `${baseUrl}/${locale}${path}`;
}

type SchemaObject =
  | Person
  | Organization
  | WebSite
  | WebPage
  | AboutPage
  | ContactPage
  | CollectionPage
  | Service
  | BreadcrumbList
  | BlogPosting
  | CaseStudy
  | FAQPage
  | HowTo;

interface WithId {
  "@id"?: string;
}

type Person = WithId & {
  "@type": "Person";
  name: string;
  url: string;
  sameAs: string[];
  image?: string;
  jobTitle?: string;
  knowsLanguage?: string[];
  worksFor?: Organization;
  email?: string;
  knowsAbout?: string[];
};

interface ContactPoint {
  "@type": "ContactPoint";
  availableLanguage?: string[];
  contactType: string;
  email: string;
  telephone?: string;
}

interface PostalAddress {
  "@type": "PostalAddress";
  addressCountry: string;
  addressLocality: string;
}

interface GeoCoordinates {
  "@type": "GeoCoordinates";
  latitude: number;
  longitude: number;
}

interface Country {
  "@type": "Country";
  name: string;
}

interface OpeningHoursSpecification {
  "@type": "OpeningHoursSpecification";
  closes: string;
  dayOfWeek: string[];
  opens: string;
}

interface ImageObject {
  "@type": "ImageObject";
  height?: number;
  url: string;
  width?: number;
}

interface Offer {
  "@type": "Offer";
  itemOffered: { "@id": string };
}

interface OfferCatalog {
  "@type": "OfferCatalog";
  itemListElement: Offer[];
  name: string;
}

type Organization = WithId & {
  "@type": "Organization" | "ProfessionalService";
  name: string;
  legalName?: string;
  url: string;
  logo?: ImageObject;
  description?: string;
  sameAs?: string[];
  contactPoint?: ContactPoint;
  telephone?: string;
  address?: PostalAddress;
  geo?: GeoCoordinates;
  openingHoursSpecification?: OpeningHoursSpecification[];
  founder?: Person | { "@id": string };
  foundingDate?: string;
  priceRange?: string;
  areaServed?: Country[];
  knowsAbout?: string[];
  hasOfferCatalog?: OfferCatalog;
};

type BlogPosting = WithId & {
  "@type": "BlogPosting";
  mainEntityOfPage: {
    "@type": "WebPage";
    "@id": string;
  };
  headline: string;
  description: string;
  image: string;
  author: Person | { "@id": string };
  publisher: Organization | { "@id": string };
  datePublished: string;
  dateModified: string;
  url: string;
  keywords?: string;
  wordCount?: number;
  articleBody?: string;
  articleSection?: string;
  inLanguage?: string;
};

type WebSite = WithId & {
  "@type": "WebSite";
  name: string;
  url: string;
  description: string;
  author: Person | { "@id": string };
  publisher: Organization | { "@id": string };
  inLanguage: string;
  copyrightYear: number;
  copyrightHolder: Person | { "@id": string };
  potentialAction?: SearchAction;
};

interface SearchAction {
  "@type": "SearchAction";
  "query-input": string;
  target: {
    "@type": "EntryPoint";
    urlTemplate: string;
  };
}

type AboutPage = WithId & {
  "@type": "AboutPage";
  name: string;
  url: string;
  description: string;
  about: { "@id": string };
  isPartOf: { "@id": string };
  inLanguage: string;
};

type ContactPage = WithId & {
  "@type": "ContactPage";
  name: string;
  url: string;
  description: string;
  isPartOf: { "@id": string };
  inLanguage: string;
};

type WebPage = WithId & {
  "@type": "WebPage";
  name: string;
  url: string;
  description: string;
  isPartOf: { "@id": string };
  about?: { "@id": string };
  mainEntity?: { "@id": string };
  inLanguage: string;
};

type Service = WithId & {
  "@type": "Service";
  name: string;
  description: string;
  provider: { "@id": string };
  areaServed: string;
  serviceType: string;
  url?: string;
};

type CollectionPage = WithId & {
  "@type": "CollectionPage";
  name: string;
  url: string;
  description: string;
  isPartOf: { "@id": string };
  inLanguage: string;
};

type BreadcrumbList = WithId & {
  "@type": "BreadcrumbList";
  itemListElement: BreadcrumbItem[];
};

interface BreadcrumbItem {
  "@type": "ListItem";
  item: string;
  name: string;
  position: number;
}

/**
 * A portfolio case study. These pages describe work delivered for a client,
 * not a distributable codebase, so they are `CreativeWork` rather than
 * `SoftwareSourceCode` — see `createCaseStudySchema`.
 */
type CaseStudy = WithId & {
  "@type": "CreativeWork";
  name: string;
  description: string;
  url: string;
  image?: string;
  author: Person | { "@id": string };
  dateCreated?: string;
  dateModified?: string;
  genre?: string;
  inLanguage?: string;
  /** Stack names. `programmingLanguage` exists only on SoftwareSourceCode. */
  keywords?: string[];
  /** The public repository, when the project has one. */
  sameAs?: string[];
};

interface Answer {
  "@type": "Answer";
  text: string;
}

interface Question {
  "@type": "Question";
  acceptedAnswer: Answer;
  name: string;
}

type FAQPage = WithId & {
  "@type": "FAQPage";
  mainEntity: Question[];
};

interface HowToStep {
  "@type": "HowToStep";
  name: string;
  position: number;
  text: string;
}

type HowTo = WithId & {
  "@type": "HowTo";
  description: string;
  name: string;
  step: HowToStep[];
};

/**
 * The subjects the studio is an authority on, in the words buyers search
 * with. Shared by the Person and the Organization so the two entities make the
 * same claim, and kept to work the site actually describes on /services and
 * /faq — `knowsAbout` is read as an assertion, not as a keyword bag.
 */
const AI_EXPERTISE = [
  "AI Automation",
  "AI Systems Engineering",
  "AI Agent Development",
  "Retrieval-Augmented Generation (RAG)",
  "AI Knowledge Bases",
  "AI Chatbot Development",
  "AI Workflow Automation",
  "n8n",
  "Large Language Model (LLM) Integration",
  "AI API Integration",
  "AI Document Processing",
  "AI Data Extraction",
  "AI Customer Support Automation",
  "AI Sales and Lead Generation Automation",
  "AI CRM Automation",
  "AI Agent Teams",
  "AI Research Agents",
  "Business Process Automation",
  "AI Consulting",
];

/**
 * The search-facing service category for each capability, in English because
 * `serviceType` names a category rather than a sentence on the page. The
 * labels themselves stay localized in `name`.
 */
const CAPABILITY_SERVICE_TYPES: Record<CapabilityId, string> = {
  "agent-teams": "AI agent development and multi-agent systems",
  "full-systems":
    "Custom AI systems development, AI integration services and AI infrastructure",
  "make-money": "AI sales and lead generation automation",
  research: "AI research agents and automated market research",
  "save-time":
    "AI workflow automation, RAG knowledge bases, AI chatbots and n8n automation",
};

const schemaIds = {
  organization: () => `${getBaseUrl()}/#organization`,
  person: () => `${getBaseUrl()}/#person`,
  webpage: (path: string) => `${localizedSchemaUrl(path)}#webpage`,
  website: () => `${getBaseUrl()}/#website`,
} as const;

/**
 * One Service node per dedicated page, at a locale-independent id: the service
 * is the same thing in both languages, only the page describing it differs.
 */
function servicePageServiceId(slug: string): string {
  return `${getBaseUrl()}/services/${slug}#service`;
}

function normalizeImageUrl(image: string | undefined): string | undefined {
  if (!image) {
    return;
  }
  if (image.startsWith("http")) {
    return image;
  }
  return `${getBaseUrl()}${image}`;
}

function createPersonSchema(options?: { includeId?: boolean }): Person {
  const schema: Person = {
    "@type": "Person",
    // The published business address, not a personal one. The Person node
    // is a public business entity here; a gmail address on it reads as an
    // unaffiliated individual and contradicts the Organization's contactPoint.
    email: siteConfig.contacts.mail,
    // No `image` until a headshot exists (#36); a company logo is wrong for a Person.
    jobTitle: "Founder & Lead Developer",
    knowsAbout: [
      ...AI_EXPERTISE,
      "TypeScript",
      "React",
      "Node.js",
      "Full Stack Development",
      "System Integration",
    ],
    knowsLanguage: [...locales],
    name: siteConfig.author.name,
    sameAs: socialConfig.map((social) => social.url),
    url: getBaseUrl(),
    worksFor: {
      "@type": "Organization",
      name: "Lindevo",
      url: getBaseUrl(),
    },
  };

  if (options?.includeId) {
    schema["@id"] = schemaIds.person();
  }

  return schema;
}

/**
 * Human-readable names for the ISO codes in `siteConfig.localBusiness.areaServed`.
 * schema.org `Country.name` expects a country name rather than a two-letter code,
 * so the config keeps the canonical identifier and the graph prints the readable
 * form. An unmapped code falls back to itself rather than disappearing.
 */
const COUNTRY_NAMES: Record<string, string> = {
  DE: "Germany",
  EE: "Estonia",
  FI: "Finland",
  LT: "Lithuania",
  LV: "Latvia",
  NO: "Norway",
  PL: "Poland",
  SE: "Sweden",
};

function createOrganizationSchema(options?: {
  includeId?: boolean;
}): Organization {
  const schema: Organization = {
    "@type": "ProfessionalService",
    address: {
      "@type": "PostalAddress",
      addressCountry: siteConfig.localBusiness.addressCountry,
      // Same city string the /legal imprint prints, so the two cannot drift.
      addressLocality: siteConfig.legal.addressPublic,
    },
    areaServed: siteConfig.localBusiness.areaServed.map((countryCode) => ({
      "@type": "Country" as const,
      name: COUNTRY_NAMES[countryCode] ?? countryCode,
    })),
    contactPoint: {
      "@type": "ContactPoint",
      availableLanguage: ["English", "Latvian"],
      contactType: "customer service",
      email: siteConfig.contacts.mail,
      telephone: siteConfig.contacts.phone,
    },
    description: siteConfig.description,
    founder: { "@id": schemaIds.person() },
    // The date the VID registration behind the trade name took effect — see
    // /legal. Sourced from config so it cannot drift from the imprint.
    foundingDate: siteConfig.legal.registeredSince,
    // Places the studio in Liepāja rather than "somewhere in Latvia", which is
    // what lets a geographically-rooted one-person shop surface locally.
    geo: {
      "@type": "GeoCoordinates",
      latitude: siteConfig.localBusiness.geo.latitude,
      longitude: siteConfig.localBusiness.geo.longitude,
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      // Each offer names what it is rather than only pointing at it. The
      // Organization ships on every page, so a bare @id reference would leave
      // an undefined node everywhere except /services — which is exactly what
      // the deleted per-service pages used to do.
      itemListElement: [
        ...getCapabilities().map((capability) => ({
          "@type": "Offer" as const,
          itemOffered: {
            "@id": `${getBaseUrl()}/services#${capability.id}`,
            "@type": "Service",
            name: capability.label,
          },
        })),
        // The dedicated pages, each defined in full on its own URL.
        ...getServicePages().map((page) => ({
          "@type": "Offer" as const,
          itemOffered: {
            "@id": servicePageServiceId(page.slug),
            "@type": "Service",
            name: page.nav,
          },
        })),
      ],
      name: "AI automation services",
    },
    knowsAbout: [
      ...AI_EXPERTISE,
      "Custom Web Applications",
      "System Integration",
    ],
    legalName: siteConfig.legal.name,
    // Raster, not the SVG mark: Google ignores SVG for rich-result logos.
    logo: {
      "@type": "ImageObject",
      height: 512,
      url: `${getBaseUrl()}/images/logo-512.png`,
      width: 512,
    },
    name: siteConfig.title,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        closes: siteConfig.localBusiness.openingHours.closes,
        dayOfWeek: siteConfig.localBusiness.openingHours.dayOfWeek,
        opens: siteConfig.localBusiness.openingHours.opens,
      },
    ],
    priceRange: siteConfig.localBusiness.priceRange,
    // The Google Business Profile ties this entity to the Maps listing. It is
    // read from `siteConfig.localBusiness.googleBusinessProfileUrl`, which may
    // be `null` — we would rather emit no entry than a guessed URL.
    sameAs: [
      ...socialConfig.map((social) => social.url),
      siteConfig.localBusiness.googleBusinessProfileUrl,
    ].filter((url): url is string => url !== null),
    telephone: siteConfig.contacts.phone,
    url: getBaseUrl(),
    // Reviews are deliberately absent. `aggregateRating` and `review` attach
    // to this node — Google reads them from the business entity, not from a
    // page wrapper — but only once real, attributable client reviews exist.
    // Inventing them is a manual-action risk and a lie; add them right here
    // when the first genuine review lands.
  };

  if (options?.includeId) {
    schema["@id"] = schemaIds.organization();
  }

  return schema;
}

function createWebSiteSchema(options?: {
  includeId?: boolean;
  useRefs?: boolean;
}): WebSite {
  const schema: WebSite = {
    "@type": "WebSite",
    author: options?.useRefs
      ? { "@id": schemaIds.person() }
      : createPersonSchema(),
    copyrightHolder: options?.useRefs
      ? { "@id": schemaIds.person() }
      : createPersonSchema(),
    copyrightYear: new Date().getFullYear(),
    description: siteConfig.description,
    inLanguage: getInLanguage(),
    name: siteConfig.title,
    potentialAction: {
      "@type": "SearchAction",
      "query-input": "required name=search_term_string",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${getBaseUrl()}/blog?q={search_term_string}`,
      },
    },
    publisher: options?.useRefs
      ? { "@id": schemaIds.organization() }
      : createOrganizationSchema(),
    url: getBaseUrl(),
  };

  if (options?.includeId) {
    schema["@id"] = schemaIds.website();
  }

  return schema;
}

function createAboutPageSchema(
  title: string,
  description: string,
  path: string
): AboutPage {
  return {
    "@id": `${localizedSchemaUrl(path)}#aboutpage`,
    "@type": "AboutPage",
    about: { "@id": schemaIds.organization() },
    description,
    inLanguage: getInLanguage(),
    isPartOf: { "@id": schemaIds.website() },
    name: title,
    url: localizedSchemaUrl(path),
  };
}

function createCollectionPageSchema(
  title: string,
  description: string,
  path: string
): CollectionPage {
  return {
    "@id": `${localizedSchemaUrl(path)}#collectionpage`,
    "@type": "CollectionPage",
    description,
    inLanguage: getInLanguage(),
    isPartOf: { "@id": schemaIds.website() },
    name: title,
    url: localizedSchemaUrl(path),
  };
}

/**
 * A plain document page — the shape /privacy-policy, /terms-of-service and
 * /legal all need. Title and description are passed in from the route's own
 * localized meta so the schema can never describe the page differently from
 * the `<title>` and `<meta name="description">` beside it.
 */
function createWebPageSchema(
  title: string,
  description: string,
  path: string
): WebPage {
  return {
    "@id": schemaIds.webpage(path),
    "@type": "WebPage",
    description,
    inLanguage: getInLanguage(),
    isPartOf: { "@id": schemaIds.website() },
    name: title,
    url: localizedSchemaUrl(path),
  };
}

function createContactPageSchema(
  title: string,
  description: string,
  path: string
): ContactPage {
  return {
    "@id": `${localizedSchemaUrl(path)}#contactpage`,
    "@type": "ContactPage",
    description,
    inLanguage: getInLanguage(),
    isPartOf: { "@id": schemaIds.website() },
    name: title,
    url: localizedSchemaUrl(path),
  };
}

function createBreadcrumbSchema(
  items: { name: string; path: string }[],
  locale?: string
): BreadcrumbList {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      item: localizedSchemaUrl(item.path, locale),
      name: item.name,
      position: index + 1,
    })),
  };
}

function createBlogPostingSchema({
  title,
  description,
  image,
  datePublished,
  dateModified,
  path,
  keywords,
  wordCount,
  articleBody,
  articleSection,
  locale,
}: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  path: string;
  keywords?: string;
  wordCount?: number;
  articleBody?: string;
  articleSection?: string;
  locale?: string;
}): BlogPosting {
  const url = localizedSchemaUrl(path, locale);
  return {
    "@id": `${url}#blogposting`,
    "@type": "BlogPosting",
    articleBody,
    articleSection,
    author: { "@id": schemaIds.person() },
    dateModified,
    datePublished,
    description,
    headline: title,
    image: image.startsWith("http") ? image : `${getBaseUrl()}${image}`,
    inLanguage: getInLanguage(locale),
    keywords,
    mainEntityOfPage: {
      "@id": url,
      "@type": "WebPage",
    },
    publisher: { "@id": schemaIds.organization() },
    url,
    wordCount,
  };
}

/**
 * A /projects page is a case study: it argues that a client's problem was
 * solved, and the code behind it is neither published nor installable. It used
 * to emit `SoftwareSourceCode`, which claims the page *is* source code and
 * pulls Google towards developer-tool intent instead of buyer intent.
 *
 * `CreativeWork` is the honest parent type and the cheapest swap: every field
 * already collected maps onto it (`name`, `description`, `url`, `image`,
 * `author`, `dateCreated`, `dateModified`), only the two code-specific
 * properties move — `programmingLanguage` becomes `keywords` and
 * `codeRepository` becomes `sameAs`. `Article` was the alternative and fits
 * worse: it would demand `headline`, `datePublished` and `publisher`, and
 * frames the page as editorial rather than as delivered work.
 */
function createCaseStudySchema({
  title,
  description,
  path,
  image,
  githubUrl,
  stacks,
  dateCreated,
  dateModified,
  status,
  locale,
}: {
  title: string;
  description: string;
  path: string;
  locale?: string;
  image?: string;
  githubUrl?: string;
  stacks?: string[];
  dateCreated?: string;
  dateModified?: string;
  /** Delivered work or an exploration. Defaults to the cautious label. */
  status?: "concept" | "delivered";
}): CaseStudy {
  const url = localizedSchemaUrl(path, locale);
  return {
    "@id": `${url}#casestudy`,
    "@type": "CreativeWork",
    author: { "@id": schemaIds.person() },
    dateCreated,
    dateModified,
    description,
    // The page says which of the two a project is, so the markup a search
    // engine reads has to say the same: calling a concept a case study claims
    // work that was never shipped.
    genre: status === "delivered" ? "Case study" : "Design concept",
    image: normalizeImageUrl(image),
    inLanguage: getInLanguage(locale),
    keywords: stacks,
    name: title,
    sameAs: githubUrl ? [githubUrl] : undefined,
    url,
  };
}

function createFaqPageSchema(
  items: FaqItem[],
  path = "/faq",
  locale?: string
): FAQPage {
  return {
    "@id": `${localizedSchemaUrl(path, locale)}#faqpage`,
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
      name: item.question,
    })),
  };
}

function createHowToSchema(): HowTo {
  return {
    "@id": `${localizedSchemaUrl("/how-we-work")}#howto`,
    "@type": "HowTo",
    description:
      "Four phases: understanding the work, building something the team can test, a careful launch with a clear handover, and support that keeps it useful.",
    name: "How Lindevo builds an AI system",
    step: getProcessPhases().map((phase, index) => ({
      "@type": "HowToStep",
      name: phase.navTitle,
      position: index + 1,
      text: phase.body[0],
    })),
  };
}

const LESS_THAN_RE = /</g;

/**
 * The JSON-LD graph, as the text of a `<script type="application/ld+json">`.
 *
 * The router writes that text into the tag as raw HTML, and an HTML parser ends
 * a script at the first `</script`, whatever its type. Article text is
 * author-controlled — `articleBody` carries the whole piece, inline code and
 * all — so a literal `</script><script>…` in a post would break out of the
 * tag. Every `<` is written as the JSON escape for U+003C (a backslash, then
 * "u003c"): the same JSON to any parser, and no `<` left for the HTML parser
 * to find.
 */
export function generateStructuredDataGraph(schemas: SchemaObject[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": schemas,
  }).replace(LESS_THAN_RE, "\\u003c");
}

export function getHomepageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    {
      "@id": `${localizedSchemaUrl("/")}#webpage`,
      "@type": "WebPage",
      about: { "@id": schemaIds.organization() },
      description: siteConfig.description,
      inLanguage: getInLanguage(),
      isPartOf: { "@id": schemaIds.website() },
      name: siteConfig.title,
      url: localizedSchemaUrl("/"),
    } as WebPage,
  ];
}

export function getAboutPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createAboutPageSchema(
      m.meta_about_title(),
      m.meta_about_description(),
      "/about"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ]),
  ];
}

export function getBlogListSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createCollectionPageSchema(
      m.meta_blog_title(),
      m.meta_blog_description(),
      "/blog"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
    ]),
  ];
}

export function getBlogPostSchemas(post: {
  title: string;
  description: string;
  image: string;
  slug: string;
  path?: string;
  datePublished: string;
  dateModified: string;
  keywords?: string;
  wordCount?: number;
  articleBody?: string;
  articleSection?: string;
  /**
   * Question/answer pairs the article itself renders. Omitted or empty means no
   * FAQPage node is emitted at all — FAQ markup has to mirror visible content.
   */
  faqItems?: FaqItem[];
  /**
   * The locale the route resolved the article in — not necessarily the request
   * locale. See `localizedSchemaUrl`. Pair it with a de-localized `path`.
   */
  locale?: string;
}): SchemaObject[] {
  const { locale } = post;
  const path = post.path ?? `/blog/${post.slug}`;
  const faqItems = post.faqItems ?? [];
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createBlogPostingSchema({
      // Component tags and code would otherwise leak into the schema's prose.
      articleBody: post.articleBody ? toPlainText(post.articleBody) : undefined,
      articleSection: post.articleSection,
      dateModified: post.dateModified,
      datePublished: post.datePublished,
      description: post.description,
      image: post.image,
      keywords: post.keywords,
      locale,
      path,
      title: post.title,
      wordCount: post.wordCount,
    }),
    createBreadcrumbSchema(
      [
        { name: "Home", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: post.title, path },
      ],
      locale
    ),
    ...(faqItems.length > 0
      ? [createFaqPageSchema(faqItems, path, locale)]
      : []),
  ];
}

export function getProjectListSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createCollectionPageSchema(
      m.meta_projects_title(),
      m.meta_projects_description(),
      "/projects"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Projects", path: "/projects" },
    ]),
  ];
}

export function getProjectSchemas(project: {
  title: string;
  description: string;
  slug: string;
  path?: string;
  image?: string;
  githubUrl?: string;
  stacks?: string[];
  dateCreated?: string;
  dateModified?: string;
  status?: "concept" | "delivered";
  /** Resolved locale, as for `getBlogPostSchemas`. */
  locale?: string;
  /** As for `getBlogPostSchemas`: only the pairs the page renders, empty for none. */
  faqItems?: FaqItem[];
}): SchemaObject[] {
  const { locale } = project;
  const path = project.path ?? `/projects/${project.slug}`;
  const faqItems = project.faqItems ?? [];
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createCaseStudySchema({
      dateCreated: project.dateCreated,
      dateModified: project.dateModified,
      description: project.description,
      githubUrl: project.githubUrl,
      image: project.image,
      locale,
      path,
      stacks: project.stacks,
      status: project.status,
      title: project.title,
    }),
    createBreadcrumbSchema(
      [
        { name: "Home", path: "/" },
        { name: "Projects", path: "/projects" },
        { name: project.title, path },
      ],
      locale
    ),
    ...(faqItems.length > 0
      ? [createFaqPageSchema(faqItems, path, locale)]
      : []),
  ];
}

export function getServicesPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createCollectionPageSchema(
      m.meta_services_title(),
      m.meta_services_description(),
      "/services"
    ),
    // Each capability is a Service node at the anchor it lives at, so the
    // Organization's offer catalog resolves.
    ...getCapabilities().map(
      (capability): Service => ({
        "@id": `${getBaseUrl()}/services#${capability.id}`,
        "@type": "Service",
        areaServed: "Europe",
        description: capability.body,
        name: capability.label,
        provider: { "@id": schemaIds.organization() },
        serviceType: CAPABILITY_SERVICE_TYPES[capability.id],
      })
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ]),
  ];
}

export function getContactPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createContactPageSchema(
      m.meta_contact_title(),
      m.meta_contact_description(),
      "/contact"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  ];
}

export function getPrivacyPolicyPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createWebPageSchema(
      m.meta_privacy_title(),
      m.meta_privacy_description(),
      "/privacy-policy"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Privacy Policy", path: "/privacy-policy" },
    ]),
  ];
}

export function getLegalPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createWebPageSchema(
      m.meta_legal_title(),
      m.meta_legal_description(),
      "/legal"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Legal Information", path: "/legal" },
    ]),
  ];
}

export function getTermsOfServicePageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createWebPageSchema(
      m.meta_terms_title(),
      m.meta_terms_description(),
      "/terms-of-service"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Terms of Service", path: "/terms-of-service" },
    ]),
  ];
}

export function getHowWeWorkPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    {
      "@id": `${localizedSchemaUrl("/how-we-work")}#webpage`,
      "@type": "WebPage",
      description: m.meta_how_we_work_description(),
      inLanguage: getInLanguage(),
      isPartOf: { "@id": schemaIds.website() },
      name: m.meta_how_we_work_title(),
      url: localizedSchemaUrl("/how-we-work"),
    },
    createHowToSchema(),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "How We Work", path: "/how-we-work" },
    ]),
  ];
}

export function getShowcasePageSchemas({
  pageName,
  description,
  path,
  isIndex = false,
}: {
  pageName: string;
  description: string;
  path: string;
  isIndex?: boolean;
}): SchemaObject[] {
  const schemas: SchemaObject[] = [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    {
      "@id": `${localizedSchemaUrl(path)}#webpage`,
      "@type": "WebPage",
      description,
      isPartOf: { "@id": schemaIds.website() },
      name: pageName,
      url: localizedSchemaUrl(path),
      ...(isIndex
        ? { mainEntity: { "@id": `${getBaseUrl()}/showcase#service` } }
        : {}),
      inLanguage: getInLanguage(),
    } as WebPage,
  ];

  if (isIndex) {
    schemas.push({
      "@id": `${getBaseUrl()}/showcase#service`,
      "@type": "Service",
      areaServed: "Europe",
      description:
        "AI systems that do real work: saving time, finding revenue, running agent teams and research, grounded in your own records, connected to your tools, inside agreed limits with a named person for exceptions.",
      name: "AI Systems Engineering",
      provider: { "@id": schemaIds.organization() },
      serviceType: "AI Systems Engineering",
    });
  }

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Showcase", path: "/showcase" },
  ];
  if (!isIndex) {
    crumbs.push({ name: pageName, path });
  }
  schemas.push(createBreadcrumbSchema(crumbs));

  return schemas;
}

export function getFaqPageSchemas(items: FaqItem[]): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    {
      "@id": `${localizedSchemaUrl("/faq")}#webpage`,
      "@type": "WebPage",
      description: m.meta_faq_description(),
      inLanguage: getInLanguage(),
      isPartOf: { "@id": schemaIds.website() },
      mainEntity: { "@id": `${localizedSchemaUrl("/faq")}#faqpage` },
      name: m.meta_faq_title(),
      url: localizedSchemaUrl("/faq"),
    } as WebPage,
    createFaqPageSchema(items),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "FAQ", path: "/faq" },
    ]),
  ];
}

export function getPainPointsPageSchemas(): SchemaObject[] {
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    createCollectionPageSchema(
      m.meta_pain_points_title(),
      m.meta_pain_points_description(),
      "/pain-points"
    ),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Pain Points", path: "/pain-points" },
    ]),
  ];
}

export function getServiceDetailPageSchemas(page: ServicePage): SchemaObject[] {
  const path = `/services/${page.slug}`;
  return [
    createPersonSchema({ includeId: true }),
    createOrganizationSchema({ includeId: true }),
    createWebSiteSchema({ includeId: true, useRefs: true }),
    {
      "@id": schemaIds.webpage(path),
      "@type": "WebPage",
      description: page.meta.description,
      inLanguage: getInLanguage(),
      isPartOf: { "@id": schemaIds.website() },
      mainEntity: { "@id": servicePageServiceId(page.slug) },
      name: page.meta.title,
      url: localizedSchemaUrl(path),
    },
    {
      "@id": servicePageServiceId(page.slug),
      "@type": "Service",
      areaServed: "Europe",
      description: page.intro,
      name: page.nav,
      provider: { "@id": schemaIds.organization() },
      serviceType: page.serviceType,
      url: localizedSchemaUrl(path),
    },
    // The questions the page renders in its own disclosures, and no others.
    createFaqPageSchema(page.faq, path),
    createBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
      { name: page.nav, path },
    ]),
  ];
}
